import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  NativeSelect,
  TextField,
  Button,
} from "@mui/material";
import SearchSelect from "../components/searchAndSelect";
import {
  getOrgsDetails,
  getPresignedUrlTransaction,
  postTransaction,
} from "../../src/services/httpCalls";
import useNotify from "../utils/useNotify";
import { useNavigate } from "react-router-dom";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { DatePicker } from "@mui/x-date-pickers";
import { allowedExtensions } from "../config/globalVar";
import { checkFileExtension } from "../utils/check-file-extension";
import { cleanObject } from "../utils/onboarding/clean-object";

// defining document type as INVOICE for all A2 transactions
const INVOICE = "INVOICE"

export default function CreateTransaction() {
  const [orgId, setOrgId] = useState();
  const [userList, setUserList] = useState([]);
  const [filesList, setFilesList] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [disableFlag, setDisableFlag] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [transForm, setTransForm] = useState({
    orgName: "",
    initiatedBy: "",
    vendorName: "",
    uploadInvoice: "",
    purposeCode: "",
    purposeDescriptionAsPerRBI: "",
    initiatedDate: "",
    proposeDate: "",
    userId: "",
    transactionType: ""
  });
  const [banks, setBanks] = useState([]);
  let notify = useNotify();
  let navigate = useNavigate();

  const selectSearchData = (e, elemName, elemId) => {
    if (elemName === "orgSelect") {
      setOrgId(e.id);
      let banks_ = []
      getOrgsDetails(e.id).then((res) => {
        if (res?.bankDetailList.length) {
          res?.bankDetailList.forEach(element => {
            if (element.isActiveForOutward === true) {
              banks_.push(element.bankName)
            }
          });
          setBanks(banks_)
        }
        if (res.userList) {
          setUserList(res.userList);
        }
        if (banks_.length === 0) {
          if (res.outwardIDFC) {
            setBanks(banks => [...banks, "IDFC"])
          }
          if (res.outwardRBL) {
            setBanks(banks => [...banks, "RBL"])
          }
          if (res.outwardDBS) {
            setBanks(banks => [...banks, "DBS" ])
          }
          if (res.outwardYes) {
            setBanks(banks => [...banks, "YES"])
          }
        }

      });
      setTransForm({ ...transForm, orgName: e.name });
    } else if (elemId === "purposeCode") {
      setTransForm({
        ...transForm,
        [elemName]: e.paymentPurposeDescriptionAsPerRBI,
        [elemId]: e.paymentPurposeCodeAsPerRBI,
        "transactionType": e.transactionType
      });
    } else {
      setTransForm({ ...transForm, [elemName]: e.name, [elemId]: `${e.id}` });
    }
  };
  const uploadFileToS3 = (url, file) => {
    return fetch(url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });
  };
  const submitTransForm = async (e) => {
    setLoadingButton(true);
    e.preventDefault();
    let date = new Date(transForm.proposeDate);
    date = date.getTime();
    let data = {
      organisationId: orgId,
      userId: transForm.initiatedBy,
      s3UrlList: filesList,
      purposeOfPayment: "",
      purposedDateOfPayment: date,
      vendorId: transForm.vendorId,
      vendorName: transForm.vendorName,
      purposeCodeAsPerRBI: transForm.purposeCode,
      purposeDescriptionAsPerRBI: transForm.purposeDescriptionAsPerRBI,
      transactionType: transForm.transactionType,
      // * product type is hard coded as outward for now
      productType: "OUTWARD",
      bankName: transForm.bankName,
    };
    let cleanData = cleanObject(data);
    if (filesList && transForm.initiatedDate && transForm.proposeDate) {
      postTransaction(cleanData)
        .then(() => {
          notify.success("Created transaction successfully");
          navigate(`/`);
        })
        .catch(() => {
          notify.error("Transaction not created successfully");
          setLoadingButton(false);
        });
    } else {
      notify.error("Kindly fill all the fields.");
    }
  };

  const fileUpload = async (files) => {
    let preSignedUrl;
    if (files.length) {
      if(!checkFileExtension(files[0].name)){
        notify.error("Please upload correct file type!");
        return;
      }
      setDisableFlag(true);
      await getPresignedUrlTransaction(files[0].name).then(async (res) => {
        preSignedUrl = res.preSignedUrl.split("?")[0];
        let invoice = {
          documentType: INVOICE,
          url: preSignedUrl,
        }
        await uploadFileToS3(res.preSignedUrl, files[0]).then(() => {
          setFilesList([...filesList, invoice]);
          setSelectedFiles([...selectedFiles, files[0].name]);
          setDisableFlag(false);
          notify.success("Uploaded the file!");
        });
      });
    }
  };

  const removeFile = (i) => {
    setDisableFlag(true);
    let filesList_ = filesList;
    let selectedFiles_ = selectedFiles;
    filesList_.splice(i, 1);
    selectedFiles_.splice(i, 1);
    setTimeout(() => {
      setFilesList(filesList_);
      setSelectedFiles(selectedFiles_);
      setDisableFlag(false);
    }, 0);
  };

  // initializing banks as empty whenever org id changes
  useEffect(() => {
    setBanks([])
  }, [orgId])

  return (
    <Box sx={{ padding: "1rem" }}>
      <Typography variant="h5" sx={{ paddingBottom: "1rem", fontWeight: 400 }}>
        Create Transaction
      </Typography>
      <Box component="form" onSubmit={(e) => submitTransForm(e)}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl
              variant="standard"
              required
              sx={{
                width: "300px",
                display: "block",
                marginTop: "0.5rem"
              }}
            >
              <InputLabel
                htmlFor="productType"
              >
                Choose Product
              </InputLabel>
              <NativeSelect
                disabled={true}
                defaultValue={"OUTWARD"}
                id="productType"
                sx={{ padding: "0.4rem", width: "100%" }}
                onChange={(e) => {
                  setTransForm({
                    ...transForm,
                    [e.target.id]: e.target.value
                  })
                }}
              >
                <option value="OUTWARD">Outward</option>
              </NativeSelect>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <SearchSelect
              selectSearchData={selectSearchData}
              label={"Select Organisation"}
              name={"orgSelect"}
              productType="OUTWARD"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl
              variant="standard"
              required
              sx={{
                width: "300px",
                display: "block",
                marginTop: "0.5rem"
              }}
              disabled={!orgId}
            >
              <InputLabel
                htmlFor="bankName"
              >
                Choose Bank
              </InputLabel>
              <NativeSelect
                id="bankName"
                sx={{ padding: "0.4rem", width: "100%" }}
                onChange={(e) => {
                  setTransForm({
                    ...transForm,
                    [e.target.id]: e.target.value
                  })
                }}
                value={transForm?.bankName ? transForm.bankName : ""}
              >
                <option value="-">--Select--</option>
                {
                  banks.map((bank) => {
                    return <option value={bank}>{bank}</option>
                  })
                }
              </NativeSelect>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl
              variant="standard"
              required
              sx={{
                width: "300px",
                display: `block`,
                marginTop: ".5rem",
              }}
            >
              <InputLabel htmlFor="initiatedBy">Select User</InputLabel>
              <NativeSelect
                disabled={transForm.orgName && userList.length ? false : true}
                id="initiatedBy"
                sx={{ padding: ".4rem", width: "100%" }}
                onChange={(e) =>
                  setTransForm({
                    ...transForm,
                    [e.target.id]: e.target.value,
                  })
                }
                value={
                  transForm?.initiatedBy !== null ? transForm?.initiatedBy : ""
                }
              >
                <option value="-">--Select--</option>
                {userList?.map((user, i) => {
                  return (
                    <option value={user?.id} key={i}>
                      {user?.name}
                    </option>
                  );
                })}
              </NativeSelect>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <SearchSelect
              selectSearchData={selectSearchData}
              label={"Select Vendor/Payer"}
              name={"vendorName"}
              id={"vendorId"}
            />
          </Grid>
          <Grid item xs={6}>
            <SearchSelect
              selectSearchData={selectSearchData}
              label={"Purpose Code"}
              id={"purposeCode"}
              name={"purposeDescriptionAsPerRBI"}
              value={
                transForm?.purposeCode &&
                `${transForm?.purposeCode}-${transForm?.purposeDescriptionAsPerRBI}`
              }
            />
          </Grid>
          <Grid item xs={6} sx={{ paddingTop: "2.5rem !important" }}>
            <DatePicker
              id="initiatedDate"
              label="Initiated Date"
              type="date"
              sx={{ width: 300 }}
              required
              onChange={(e) =>
                setTransForm({
                  ...transForm,
                  initiatedDate: e,
                })
              }
              renderInput={(params) => (
                <TextField {...params} sx={{ width: "300px" }} helperText=" " />
              )}
              value={transForm?.initiatedDate}
            />
          </Grid>
          <Grid item xs={6} sx={{ paddingTop: "2.5rem !important" }}>
            <DatePicker
              id="proposeDate"
              label="Propose/Due Date"
              type="date"
              sx={{ width: 300 }}
              required
              onChange={(e) =>
                setTransForm({
                  ...transForm,
                  proposeDate: e,
                })
              }
              renderInput={(params) => (
                <TextField {...params} sx={{ width: "300px" }} helperText=" " />
              )}
              value={transForm?.proposeDate}
              disablePast
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              component="label"
              sx={{ marginTop: "1rem" }}
              disabled={disableFlag}
            >
              Upload File
              <input
                type="file"
                onChange={(e) => fileUpload(e.target.files)}
                hidden
              />
            </Button>
            <Box sx={{ padding: "1rem 0" }}>
              {selectedFiles.map((item, i) => {
                return (
                  <Typography
                    key={i}
                    sx={{
                      fontSize: "14px",
                      color: "#222",
                      letterSpacing: ".4px",
                      marginBottom: ".75rem",
                    }}
                  >
                    {i + 1 + ") "} {item}{" "}
                    <DeleteOutlineOutlinedIcon
                      sx={{
                        height: "18px",
                        color: "#d10303",
                        marginLeft: "1rem",
                        cursor: "pointer",
                      }}
                      onClick={() => removeFile(i)}
                    />
                  </Typography>
                );
              })}
            </Box>
          </Grid>
        </Grid>
        <Box
          sx={{
            position: "fixed",
            bottom: "0",
            right: "0",
            width: "100%",
            bgcolor: "#fff",
            borderTop: "1px solid #f2f2f2",
            padding: "1rem",
            textAlign: "right",
          }}
        >
          <Button type="submit" variant="contained" disabled={loadingButton}>
            Create Transaction
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
